import React, { useState } from 'react';
import { Check, Circle, ChevronDown, ChevronUp } from 'lucide-react';

interface TestCreationProgressProps {
  currentStep: 'create' | 'preview' | 'schedule' | 'assign';
  testTitle?: string;
  className?: string;
}

const TestCreationProgress: React.FC<TestCreationProgressProps> = ({
  currentStep,
  testTitle,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const steps = [
    {
      key: 'create',
      title: 'Create Test',
      description: 'Basic details and questions',
      shortTitle: 'Create'
    },
    {
      key: 'preview',
      title: 'Preview Questions',
      description: 'Review selected questions',
      shortTitle: 'Preview'
    },
    {
      key: 'schedule',
      title: 'Schedule Test',
      description: 'Set timing and availability',
      shortTitle: 'Schedule'
    },
    {
      key: 'assign',
      title: 'Assign Test',
      description: 'Select classes and students',
      shortTitle: 'Assign'
    }
  ];

  const getCurrentStepIndex = () => {
    return steps.findIndex(step => step.key === currentStep);
  };

  const getStepStatus = (stepIndex: number) => {
    const currentIndex = getCurrentStepIndex();
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  const getStepIcon = (stepIndex: number, status: string) => {
    if (status === 'completed') {
      return <Check className="h-5 w-5 text-white" />;
    }
    return (
      <span className={`text-sm font-semibold ${
        status === 'current' ? 'text-white' : 'text-gray-500'
      }`}>
        {stepIndex + 1}
      </span>
    );
  };

  const getStepClasses = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500 border-green-500';
      case 'current':
        return 'bg-blue-500 border-blue-500';
      default:
        return 'bg-white border-gray-300';
    }
  };

  const getConnectorClasses = (stepIndex: number) => {
    const currentIndex = getCurrentStepIndex();
    return stepIndex < currentIndex
      ? 'bg-green-500'
      : 'bg-gray-300';
  };

  return (
    <div className={`bg-white border-b sticky top-0 z-40 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Collapsed Header */}
        <div
          className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center space-x-4">
            {/* Test Title */}
            {testTitle && (
              <h2 className="text-lg font-semibold text-gray-900">
                Creating: {testTitle}
              </h2>
            )}

            {/* Compact Progress Indicator */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                Step {getCurrentStepIndex() + 1} of {steps.length}
              </span>
              <div className="flex items-center space-x-1">
                {steps.map((step, index) => {
                  const status = getStepStatus(index);
                  return (
                    <div
                      key={step.key}
                      className={`
                        w-6 h-6 rounded-full border flex items-center justify-center text-xs
                        ${getStepClasses(status)}
                      `}
                      title={`${step.title}: ${step.description}`}
                    >
                      {status === 'completed' ? (
                        <Check className="h-3 w-3" />
                      ) : (
                        <span className="text-xs font-medium">{index + 1}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Expand/Collapse Button */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              {isExpanded ? 'Hide details' : 'Show details'}
            </span>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="mt-6 space-y-6">
            {/* Detailed Progress Steps */}
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const status = getStepStatus(index);
                const isLast = index === steps.length - 1;

                return (
                  <div key={step.key} className="flex items-center flex-1">
                    {/* Step Circle */}
                    <div className="flex flex-col items-center">
                      <div
                        className={`
                          w-10 h-10 rounded-full border-2 flex items-center justify-center
                          ${getStepClasses(status)}
                        `}
                      >
                        {getStepIcon(index, status)}
                      </div>

                      {/* Step Info */}
                      <div className="mt-3 text-center">
                        <div className={`text-sm font-medium ${
                          status === 'current'
                            ? 'text-blue-600'
                            : status === 'completed'
                            ? 'text-green-600'
                            : 'text-gray-500'
                        }`}>
                          {step.title}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 max-w-[120px]">
                          {step.description}
                        </div>
                      </div>
                    </div>

                    {/* Connector Line */}
                    {!isLast && (
                      <div className="flex-1 mx-4">
                        <div className={`h-0.5 ${getConnectorClasses(index)}`} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Current Step Indicator */}
            <div className="p-4 bg-blue-50 rounded border border-blue-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Circle className="h-5 w-5 text-blue-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Step {getCurrentStepIndex() + 1} of {steps.length}: {steps[getCurrentStepIndex()]?.title}
                  </h3>
                  <p className="text-sm text-blue-700">
                    {steps[getCurrentStepIndex()]?.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestCreationProgress;
